import React, { useEffect } from 'react';

import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import clsx from 'clsx';

import { logger } from '../../../core/services/loggerService';
import { Button } from '../../atoms/Button';
import { Typography } from '../../atoms/Typography';

type Variant = 'panel' | 'page';

type Props = {
  action?: {
    onClick: () => void;
    text: JSX.Element | string;
  };
  className?: string;
  dataFd: string;
  description: JSX.Element | string;
  icon: JSX.Element;
  isDarkIconBg?: boolean;
  isNoAppDesignScheme?: boolean;
  logMessage?: string;
  title?: JSX.Element | string;
  variant: Variant;
};

const useStyles = (isNoAppDesignScheme: boolean, isDarkIconBg: boolean) =>
  makeStyles((theme: Theme) => ({
    button: {
      color: isNoAppDesignScheme ? 'black' : theme.palette.common.white,
      fontSize: theme.typography.pxToRem(12),
      height: 36,
      marginTop: theme.spacing(3),
      whiteSpace: 'nowrap',
      width: 120,
    },
    description: {
      color: theme.palette.common.black,
      fontSize: theme.typography.pxToRem(12),
    },
    icon: {
      '& .MuiSvgIcon-root': {
        height: 48,
        width: 48,
      },
    },
    iconContainer: {
      alignItems: 'center',
      backgroundColor: isDarkIconBg
        ? theme.palette.grey[400]
        : theme.palette.grey[200],
      borderRadius: 100,
      display: 'flex',
      flexDirection: 'column',
      height: 96,
      justifyContent: 'center',
      marginBottom: theme.spacing(2),
      width: 96,
    },
    root: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      width: '100%',

      '&.variant': {
        '&--page': {
          minHeight: '100vh',
          overflow: 'hidden',
        },
        '&--panel': {
          margin: theme.spacing(13, 0),
        },
      },
    },
    title: {
      fontSize: theme.typography.pxToRem(16),
      fontWeight: theme.typography.fontWeightMedium,
    },

    [theme.breakpoints.up('sm')]: {
      root: {
        '&.variant': {
          '&--panel': {
            margin: 0,
            minHeight: 360,
          },
        },
      },
    },
  }));

export const Fallback = (props: Props): JSX.Element => {
  const {
    action,
    className = '',
    dataFd,
    description,
    icon,
    isDarkIconBg = false,
    isNoAppDesignScheme = false,
    logMessage = '',
    title,
    variant,
  } = props;
  const classes = useStyles(isNoAppDesignScheme, isDarkIconBg)();

  // Check if the icon has a viewBox set and if it does not set it to the default
  const iconWithViewBox = React.cloneElement(icon, {
    ...(icon.props.viewBox ? {} : { viewBox: '0 0 48 48' }),
  });

  const rootStyles = clsx(
    classes.root,
    'fd-fallback',
    variant === 'page' && 'variant--page',
    variant === 'panel' && 'variant--panel',
    className,
  );

  useEffect(() => {
    if (logMessage) {
      logger.warn(logMessage);
    }
  }, []);

  return (
    <div className={rootStyles} data-fd={dataFd}>
      <div className={classes.iconContainer} data-fd="fallback-icon-container">
        <div className={classes.icon}>{iconWithViewBox}</div>
      </div>

      {title && (
        <Typography className={classes.title} data-fd="title">
          {title}
        </Typography>
      )}
      <Typography className={classes.description} variant="caption">
        {description}
      </Typography>

      {action && (
        <Button className={classes.button} onClick={action.onClick}>
          {action.text}
        </Button>
      )}
    </div>
  );
};
